import React, { useState, useCallback, useEffect, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  TrendingUp, TrendingDown, Minus, DollarSign, ShoppingCart, 
  XCircle, MoreVertical, GripHorizontal, LayoutGrid, FileText, Filter
} from "lucide-react";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import CustomizePanel from "./CustomizePanel";
import { DashboardStatsSkeleton } from "./SkeletonLoader";
import { useSelector } from "react-redux";
import FilterDrawer from "./FilterDrawer";
import SelectDropdown from "../../customStyles/SelectDropdown";

const DashboardStats = ({
  viewType = "all",
  userId = null,
  userName = "",
  tabId = "",
}) => {
  const user = useSelector((state) => state?.user?.user);
  const componentKey = "dashboardStats";
  const contextKey = tabId || viewType; 
  const stateKey = `${componentKey}_${contextKey}`;

  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem(`${stateKey}_activeTab`) || "all");
  const [activeVertical, setActiveVertical] = useState(() => sessionStorage.getItem(`${stateKey}_activeVertical`) || "all");
  const [filterType, setFilterType] = useState(() => sessionStorage.getItem(`${stateKey}_filterType`) || "fiscal");
  const [dateRange, setDateRange] = useState(() => {
    const saved = sessionStorage.getItem(`${stateKey}_dateRange`);
    return saved ? JSON.parse(saved) : { from: "", to: "" };
  });

  const [showFilters, setShowFilters] = useState(false);
  const [tempUser, setTempUser] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [tempFilterType, setTempFilterType] = useState(filterType);
  const [tempDateRange, setTempDateRange] = useState({ from: "", to: "" });
  
  const [users, setUsers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);

  // Card layout persistence
  const [cardOrder, setCardOrder] = useState(() => {
    const uId = user?._id || "default";
    const saved = localStorage.getItem(`${componentKey}_cardOrder_${uId}`);
    const defaultOrder = ["totalDeals", "revenue", "quotationValue", "lostDeals"];
    if (saved) {
      const parsed = JSON.parse(saved);
      const missingCards = defaultOrder.filter((card) => !parsed.includes(card));
      return [...parsed, ...missingCards];
    }
    return defaultOrder;
  });

  const [hiddenCards, setHiddenCards] = useState(() => {
    const uId = user?._id || "default";
    const saved = localStorage.getItem(`${componentKey}_hiddenCards_${uId}`);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => { sessionStorage.setItem(`${stateKey}_activeTab`, activeTab); }, [activeTab, stateKey]);
  useEffect(() => { sessionStorage.setItem(`${stateKey}_activeVertical`, activeVertical); }, [activeVertical, stateKey]);
  useEffect(() => { sessionStorage.setItem(`${stateKey}_filterType`, filterType); }, [filterType, stateKey]);
  useEffect(() => { sessionStorage.setItem(`${stateKey}_dateRange`, JSON.stringify(dateRange)); }, [dateRange, stateKey]);

  useEffect(() => {
    setActiveTab(sessionStorage.getItem(`${stateKey}_activeTab`) || "all");
    setActiveVertical(sessionStorage.getItem(`${stateKey}_activeVertical`) || "all");
    setFilterType(sessionStorage.getItem(`${stateKey}_filterType`) || "fiscal");
    const savedRange = sessionStorage.getItem(`${stateKey}_dateRange`);
    setDateRange(savedRange ? JSON.parse(savedRange) : { from: "", to: "" });
  }, [viewType, tabId, stateKey]);

  const dashboardTitle = useMemo(() => {
    if (viewType === "user" && userName) return `${userName}'s Overview`;
    return "Overview";
  }, [viewType, userName]);

  // Fetch divisions and business verticals
  const fetchDivisionsAndVerticals = useCallback(async () => {
    try {
      const [divRes, vertRes] = await Promise.all([
        api.get(SummaryApi.getAllDivisions?.url),
        api.get(SummaryApi.getAllProducts?.url),
      ]);
      if (divRes.data?.success) setDivisions(divRes.data.data || []);
      if (vertRes.data?.success) setVerticals(vertRes.data.data || []);
    } catch (error) {
      console.error("Failed to fetch divisions/verticals:", error);
    }
  }, []);

  // Filter verticals based on active division
  const availableVerticals = useMemo(() => {
    if (activeTab === "all") return verticals;
    return verticals.filter(v => (v.divisionId?._id || v.divisionId) === activeTab);
  }, [verticals, activeTab]);

  const handleTabChange = (divId) => {
    setActiveTab(divId);
    setActiveVertical("all");
  };

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        filterType,
        ...(viewType === "user" ? { userId } : selectedUser ? { userId: selectedUser } : {}),
        ...(activeTab !== "all" && { divisionId: activeTab }),
        ...(activeVertical !== "all" && { verticalId: activeVertical }),
        ...(dateRange.from && { fromDate: dateRange.from }),
        ...(dateRange.to && { toDate: dateRange.to }),
      });
      const response = await api.get(`${SummaryApi.dashboardStats.url}?${params.toString()}`);
      if (response.data?.success) setDashboardData(response.data.data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  }, [filterType, viewType, userId, selectedUser, activeTab, activeVertical, dateRange.from, dateRange.to]);

  const fetchUsers = async () => {
    try {
      const response = await api.get(SummaryApi.executiveUsers?.url);
      if (response.data?.success) {
        const formattedUsers = (response.data.data || []).map((u) => {
          const parts = (u.fullName || "").trim().split(" ");
          return { ...u, _id: u.userId, firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "" };
        });
        setUsers(formattedUsers);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
    }
  };

  useEffect(() => { fetchUsers(); fetchDivisionsAndVerticals(); }, [fetchDivisionsAndVerticals]);
  useEffect(() => { fetchDashboardStats(); }, [fetchDashboardStats]);

  useEffect(() => {
    if (user?._id) localStorage.setItem(`${componentKey}_cardOrder_${user._id}`, JSON.stringify(cardOrder));
  }, [cardOrder, user, componentKey]);

  useEffect(() => {
    if (user?._id) localStorage.setItem(`${componentKey}_hiddenCards_${user._id}`, JSON.stringify(hiddenCards));
  }, [hiddenCards, user, componentKey]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(cardOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setCardOrder(items);
  };

  const toggleCardVisibility = (cardId) => {
    setHiddenCards((prev) => prev.includes(cardId) ? prev.filter((id) => id !== cardId) : [...prev, cardId]);
  };

  const resetLayout = () => {
    setCardOrder(["totalDeals", "revenue", "quotationValue", "lostDeals"]);
    setHiddenCards([]);
  };

  const getIcon = (type) => {
    const icons = {
      totalDeals: <ShoppingCart className="h-6 w-6 text-red-600" />,
      revenue: <DollarSign className="h-6 w-6 text-green-600" />,
      quotationValue: <FileText className="h-6 w-6 text-blue-600" />,
      lostDeals: <XCircle className="h-6 w-6 text-gray-600" />,
    };
    return icons[type];
  };

  const getIconBgColor = (type) => {
    const colors = {
      totalDeals: "bg-red-50",
      revenue: "bg-green-50",
      quotationValue: "bg-blue-50",
      lostDeals: "bg-gray-50",
    };
    return colors[type] || "bg-gray-50";
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up": return <TrendingUp className="h-4 w-4 text-green-600" />;
      case "down": return <TrendingDown className="h-4 w-4 text-red-600" />;
      default: return <Minus className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatValue = (type, data) => {
    if (!data) return "0";
    if (type === "revenue" || type === "quotationValue") return `₹${(data.amount || 0).toLocaleString("en-IN")}`;
    return (data.count || 0).toLocaleString("en-IN");
  };

  const getCardTitle = (type) => {
    const titles = {
      totalDeals: viewType === "user" ? "My Leads" : "Total Leads",
      revenue: viewType === "user" ? "My Revenue" : "Total Revenue",
      quotationValue: viewType === "user" ? "My Quotations" : "Total Quotations",
      lostDeals: viewType === "user" ? "My Lost Leads" : "Lost Leads",
    };
    return titles[type];
  };

  const renderMetricCard = (type, data) => {
    if (hiddenCards.includes(type) || !data) return null;

    return (
      <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-5 hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2.5 rounded-lg ${getIconBgColor(type)}`}>{getIcon(type)}</div>
          <button className="p-1 hover:bg-gray-100 rounded text-gray-400"><MoreVertical className="h-4 w-4" /></button>
        </div>
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-gray-600">{getCardTitle(type)}</p>
          <p className="text-xl md:text-2xl font-bold text-gray-900">{formatValue(type, data)}</p>
          <div className="flex items-center gap-1.5 text-xs">
            {getTrendIcon(data.trend)}
            <span className={`font-semibold ${data.trend === "up" ? "text-green-600" : data.trend === "down" ? "text-red-600" : "text-gray-500"}`}>
              {Math.abs(data.percentage || 0)}%
            </span>
            <span className="text-gray-400">vs last period</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <DashboardStatsSkeleton variant="shimmer" />;

  return (
    <div className="space-y-4">
      {/* Header with Title and Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{dashboardTitle}</h1>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Vertical Filter Dropdown */}
          <div className="w-44 sm:w-48">
            <SelectDropdown
              options={[{ _id: 'all', name: 'All Verticals' }, ...availableVerticals]}
              value={activeVertical}
              onChange={(val) => setActiveVertical(val)}
              placeholder="Select Vertical"
              valueKey="_id"
              labelKey="name"
              searchable={true}
              className="w-full text-xs"
            />
          </div>

          <button
            onClick={() => {
              setTempUser(selectedUser);
              setTempFilterType(filterType);
              setTempDateRange(dateRange);
              setShowFilters(true);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs md:text-sm font-medium"
          >
            <Filter size={15} />
            Filters
          </button>

          <button onClick={() => setIsCustomizeOpen(true)} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600" title="Customize Cards">
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Division Navigation Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto no-scrollbar mb-4">
        <nav className="-mb-px flex space-x-6">
          <button
            onClick={() => handleTabChange("all")}
            className={`py-2 px-1 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap transition-colors ${
              activeTab === "all" ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            All Divisions
          </button>
          {divisions.map((division) => (
            <button
              key={division._id}
              onClick={() => handleTabChange(division._id)}
              className={`py-2 px-1 border-b-2 font-medium text-xs md:text-sm whitespace-nowrap transition-colors ${
                activeTab === division._id ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {division.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Draggable Overview Cards */}
      {dashboardData && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="metrics" direction="horizontal">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {cardOrder.map((cardType, index) =>
                  dashboardData[cardType] && !hiddenCards.includes(cardType) && (
                    <Draggable key={cardType} draggableId={cardType} index={index}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} className={`${snapshot.isDragging ? "opacity-50" : ""}`}>
                          <div className="relative group">
                            <div {...provided.dragHandleProps} className="absolute -top-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                              <GripHorizontal className="h-4 w-4 text-gray-400" />
                            </div>
                            {renderMetricCard(cardType, dashboardData[cardType])}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  )
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Stage Cards: Fully Controlled by dashboardConfig */}
      {dashboardData?.stages && dashboardData.stages.length > 0 && (
        <div className="mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">{viewType === "user" ? "My Pipeline Stages" : "Stage Pipeline"}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {dashboardData.stages.map((stage) => {
              const cfg = stage.dashboardConfig || {};
              const totalPipelineLeads = dashboardData.totalDeals?.count || 1;
              const leadShare = Math.round(((stage.totalDeals || 0) / totalPipelineLeads) * 100);

              return (
                <div
                  key={stage.stageId}
                  className="bg-white rounded-xl shadow-xs border border-gray-200 p-5 hover:shadow-sm transition-shadow flex flex-col justify-between"
                  style={{ borderTopColor: stage.color || "#e5e7eb", borderTopWidth: "4px" }}
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color || "#e5e7eb" }} />
                        <span className="text-xs font-semibold text-gray-700">#{stage.orderSequence}</span>
                      </div>
                      <span className="text-[10px] font-bold text-gray-400 uppercase">{stage.type || "Stage"}</span>
                    </div>

                    <h3 className="font-bold text-sm text-gray-900 mb-0.5">{stage.stageName}</h3>
                    <p className="text-xs text-gray-500 mb-3 font-mono font-medium">{stage.stageId}</p>

                    <div className="space-y-2 text-xs">
                      {/* Leads Count: Hidden completely if showLeadCount === false */}
                      {cfg.showLeadCount !== false && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Leads</span>
                          <span className="font-bold text-gray-900">{stage.totalDeals || 0}</span>
                        </div>
                      )}

                      {/* Revenue: Hidden completely if showRevenue === false */}
                      {cfg.showRevenue !== false && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Revenue</span>
                          <span className="font-bold text-gray-800">₹{(stage.totalAmount || 0).toLocaleString("en-IN")}</span>
                        </div>
                      )}

                      {/* Quotations: Shown only if showQuotation === true */}
                      {cfg.showQuotation === true && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500">Quotations</span>
                          <span className="font-bold text-blue-600">₹{(stage.quotationValue || 0).toLocaleString("en-IN")}</span>
                        </div>
                      )}

                      {/* Dynamic Stage Share %: Hidden if showPercentage === false */}
                      {cfg.showPercentage !== false && (
                        <div className="flex justify-between items-center pt-1 border-t border-gray-100">
                          <span className="text-gray-500">Stage Share</span>
                          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-bold text-xs">{leadShare}%</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Avg. Lead Value: Only shown if revenue is enabled and leads exist */}
                  {stage.totalDeals > 0 && cfg.showRevenue !== false && (
                    <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px]">
                      <span className="text-gray-400">Avg. Lead Value</span>
                      <span className="font-semibold text-gray-700">
                        ₹{Math.round((stage.totalAmount || 0) / stage.totalDeals).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Panels */}
      <CustomizePanel
        isOpen={isCustomizeOpen}
        onClose={() => setIsCustomizeOpen(false)}
        hiddenCards={hiddenCards}
        toggleCardVisibility={toggleCardVisibility}
        resetLayout={resetLayout}
      />
      
      <FilterDrawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        showUserFilter={viewType !== "user"}
        users={users}
        selectedUser={tempUser}
        onUserChange={setTempUser}
        showDateFilter={true}
        filterType={tempFilterType}
        onFilterTypeChange={setTempFilterType}
        dateRange={tempDateRange}
        onDateRangeChange={setTempDateRange}
        onReset={() => {
          setTempUser("");
          setSelectedUser("");
          setTempFilterType("fiscal");
          setFilterType("fiscal");
          setTempDateRange({ from: "", to: "" });
          setDateRange({ from: "", to: "" });
          setShowFilters(false);
        }}
        onApply={() => {
          setSelectedUser(tempUser);
          setFilterType(tempFilterType);
          setDateRange(tempDateRange);
        }}
      />
    </div>
  );
};

export default DashboardStats;


// import React, { useState, useCallback, useEffect, useMemo } from "react";
// import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
// import {
//   TrendingUp,
//   TrendingDown,
//   Minus,
//   DollarSign,
//   ShoppingCart,
//   XCircle,
//   MoreVertical,
//   GripHorizontal,
//   LayoutGrid,
//   FileText, // Imported for the new Quotation metric
//   Filter,
// } from "lucide-react";
// import api from "../../common/apiClient";
// import SummaryApi from "../../common/SummaryApi";
// import CustomizePanel from "./CustomizePanel";
// import { DashboardStatsSkeleton } from "./SkeletonLoader";
// import { useSelector } from "react-redux";
// import FilterDrawer from "./FilterDrawer";

// const DashboardStats = ({
//   viewType = "all",
//   userId = null,
//   userName = "",
//   tabId = "",
// }) => {
//   const user = useSelector((state) => state?.user?.user);
//   const componentKey = "dashboardStats";
//   const contextKey = tabId || viewType; 
//   const stateKey = `${componentKey}_${contextKey}`;

//   const [activeTab, setActiveTab] = useState(() => {
//     const saved = sessionStorage.getItem(`${stateKey}_activeTab`);
//     return saved || "all";
//   });

//   const [filterType, setFilterType] = useState(() => {
//     const saved = sessionStorage.getItem(`${stateKey}_filterType`);
//     return saved || "fiscal";
//   });

//   const [dateRange, setDateRange] = useState(() => {
//     const saved = sessionStorage.getItem(`${stateKey}_dateRange`);
//     return saved ? JSON.parse(saved) : { from: "", to: "" };
//   });
//   const [showFilters, setShowFilters] = useState(false);

//   const [tempUser, setTempUser] = useState("");
//   const [selectedUser, setSelectedUser] = useState("");
//   const [tempFilterType, setTempFilterType] = useState(filterType);
//   const [tempDateRange, setTempDateRange] = useState({ from: "", to: "" });
  
//   const [users, setUsers] = useState([]);
//   const [divisions, setDivisions] = useState([]);
//   const [dashboardData, setDashboardData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);

//   const showDivisionTabs = true;

//   // FIX: Merges new quotationValue card dynamically for users with existing saved layout preferences
//   const [cardOrder, setCardOrder] = useState(() => {
//     const userId = user._id;
//     const saved = localStorage.getItem(`${componentKey}_cardOrder_${userId}`);
//     const defaultOrder = ["totalDeals", "revenue", "quotationValue", "lostDeals"];
    
//     if (saved) {
//       const parsed = JSON.parse(saved);
//       const missingCards = defaultOrder.filter((card) => !parsed.includes(card));
//       return [...parsed, ...missingCards];
//     }
//     return defaultOrder;
//   });

//   const [hiddenCards, setHiddenCards] = useState(() => {
//     const userId = user._id;
//     const saved = localStorage.getItem(`${componentKey}_hiddenCards_${userId}`);
//     return saved ? JSON.parse(saved) : [];
//   });

//   useEffect(() => {
//     sessionStorage.setItem(`${stateKey}_activeTab`, activeTab);
//   }, [activeTab, stateKey]);

//   useEffect(() => {
//     sessionStorage.setItem(`${stateKey}_filterType`, filterType);
//   }, [filterType, stateKey]);

//   useEffect(() => {
//     sessionStorage.setItem(`${stateKey}_dateRange`, JSON.stringify(dateRange));
//   }, [dateRange, stateKey]);

//   const dashboardTitle = useMemo(() => {
//     if (viewType === "user" && userName) {
//       return `${userName}'s Overview`;
//     }
//     return "Overview";
//   }, [viewType, userName]);

//   const fetchDivisions = useCallback(async () => {
//     try {
//       const response = await api.get(SummaryApi.getAllDivisions.url);
//       if (response.data.success) {
//         setDivisions(response.data.data || []);
//       }
//     } catch (error) {
//       console.error("Failed to fetch divisions:", error);
//     }
//   }, []);

//   const fetchDashboardStats = useCallback(async () => {
//     setLoading(true);
//     try {
//       const params = new URLSearchParams({
//         filterType,
//         ...(viewType === "user"
//           ? { userId }
//           : selectedUser
//             ? { userId: selectedUser }
//             : {}),
//         ...(activeTab !== "all" && { divisionId: activeTab }),
//         ...(dateRange.from && { fromDate: dateRange.from }),
//         ...(dateRange.to && { toDate: dateRange.to }),
//       });
//       const response = await api.get(
//         `${SummaryApi.dashboardStats.url}?${params.toString()}`,
//       );
//       if (response.data.success) {
//         setDashboardData(response.data.data);
//       }
//     } catch (error) {
//       console.error("Failed to fetch dashboard stats:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [filterType, viewType, userId, selectedUser, activeTab, dateRange.from, dateRange.to]);

//   const fetchUsers = async () => {
//     try {
//       const response = await api.get(SummaryApi.executiveUsers.url);

//       if (response.data.success) {
//         const usersData = response.data.data || [];

//         const formattedUsers = usersData.map((user) => {
//           const nameParts = (user.fullName || "").trim().split(" ");

//           return {
//             ...user,
//             _id: user.userId,
//             firstName: nameParts[0] || "",
//             lastName: nameParts.slice(1).join(" ") || "",
//           };
//         });

//         setUsers(formattedUsers);
//       }
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//       setUsers([]);
//     }
//   };

//   useEffect(() => {
//     fetchUsers();
//   }, []);

//   useEffect(() => {
//     fetchDivisions();
//   }, [fetchDivisions]);

//   useEffect(() => {
//     fetchDashboardStats();
//   }, [fetchDashboardStats]);

//   useEffect(() => {
//     const userId = user._id;
//     localStorage.setItem(
//       `${componentKey}_cardOrder_${userId}`,
//       JSON.stringify(cardOrder),
//     );
//   }, [cardOrder, user, componentKey]);

//   useEffect(() => {
//     const userId = user._id;
//     localStorage.setItem(
//       `${componentKey}_hiddenCards_${userId}`,
//       JSON.stringify(hiddenCards),
//     );
//   }, [hiddenCards, user, componentKey]);

//   useEffect(() => {
//     const savedTab = sessionStorage.getItem(`${stateKey}_activeTab`);
//     setActiveTab(savedTab || "all");

//     const savedFilter = sessionStorage.getItem(`${stateKey}_filterType`);
//     setFilterType(savedFilter || "fiscal");

//     const savedRange = sessionStorage.getItem(`${stateKey}_dateRange`);
//     setDateRange(savedRange ? JSON.parse(savedRange) : { from: "", to: "" });
//   }, [viewType, tabId, stateKey]);

//   const handleDragEnd = (result) => {
//     if (!result.destination) return;

//     const items = Array.from(cardOrder);
//     const [reorderedItem] = items.splice(result.source.index, 1);
//     items.splice(result.destination.index, 0, reorderedItem);

//     setCardOrder(items);
//   };

//   const toggleCardVisibility = (cardId) => {
//     setHiddenCards((prev) =>
//       prev.includes(cardId)
//         ? prev.filter((id) => id !== cardId)
//         : [...prev, cardId],
//     );
//   };

//   const resetLayout = () => {
//     setCardOrder(["totalDeals", "revenue", "quotationValue", "lostDeals"]);
//     setHiddenCards([]);
//   };

//   const getIcon = (type) => {
//     const icons = {
//       totalDeals: <ShoppingCart className="h-6 w-6 text-red-600" />,
//       revenue: <DollarSign className="h-6 w-6 text-green-600" />,
//       quotationValue: <FileText className="h-6 w-6 text-blue-600" />, // Added icon for quotation
//       lostDeals: <XCircle className="h-6 w-6 text-gray-600" />,
//     };
//     return icons[type];
//   };

//   const getIconBgColor = (type) => {
//     const colors = {
//       totalDeals: "bg-red-50",
//       revenue: "bg-green-50",
//       quotationValue: "bg-blue-50", // Styling for quotation background
//       lostDeals: "bg-gray-50",
//     };
//     return colors[type] || "bg-gray-50";
//   };

//   const getTrendIcon = (trend) => {
//     switch (trend) {
//       case "up":
//         return <TrendingUp className="h-4 w-4 text-green-600" />;
//       case "down":
//         return <TrendingDown className="h-4 w-4 text-red-600" />;
//       default:
//         return <Minus className="h-4 w-4 text-gray-500" />;
//     }
//   };

//   const formatValue = (type, data) => {
//     if (!data) return "0";
//     if (type === "revenue" || type === "quotationValue") {
//       return `₹${(data.amount || 0).toLocaleString("en-IN")}`;
//     }
//     return (data.count || 0).toLocaleString("en-IN");
//   };

//   const getCardTitle = (type) => {
//     const titles = {
//       totalDeals: viewType === "user" ? "My Clients" : "Total Clients",
//       revenue: viewType === "user" ? "My Revenue" : "Total Revenue",
//       quotationValue: viewType === "user" ? "My Quotation" : "Total Quotation",
//       lostDeals: viewType === "user" ? "My Lost Clients" : "Lost Clients",
//     };
//     return titles[type];
//   };

//   const renderMetricCard = (type, data) => {
//     if (hiddenCards.includes(type) || !data) return null;

//     return (
//       <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
//         <div className="flex items-start justify-between mb-4">
//           <div className={`p-3 rounded-lg ${getIconBgColor(type)}`}>
//             {getIcon(type)}
//           </div>
//           <button className="p-1 hover:bg-gray-100 rounded">
//             <MoreVertical className="h-4 w-4 text-gray-400" />
//           </button>
//         </div>

//         <div className="space-y-2">
//           <p className="text-sm font-medium text-gray-600">
//             {getCardTitle(type)}
//           </p>
//           <p className="text-2xl font-bold text-gray-900">
//             {formatValue(type, data)}
//           </p>

//           <div className="flex items-center gap-2">
//             {getTrendIcon(data.trend)}
//             <span
//               className={`text-sm font-medium ${
//                 data.trend === "up"
//                   ? "text-green-600"
//                   : data.trend === "down"
//                     ? "text-red-600"
//                     : "text-gray-500"
//               }`}
//             >
//               {Math.abs(data.percentage || 0)}%
//             </span>
//             <span className="text-xs text-gray-500">vs last period</span>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   if (loading) {
//     return <DashboardStatsSkeleton variant="shimmer" />;
//   }

//   return (
//     <div className="space-y-4">
//       {/* Header */}
//       <div className="flex justify-between items-center mb-6">
//         <h1 className="text-2xl font-bold text-gray-900">{dashboardTitle}</h1>

//         <div className="flex items-center gap-3">
//           <button
//             onClick={() => {
//               setTempUser(selectedUser);
//               setTempFilterType(filterType);
//               setTempDateRange(dateRange);
//               setShowFilters(true);
//             }}
//             className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
//           >
//             <Filter size={16} />
//             Filters
//           </button>

//           <button
//             onClick={() => setIsCustomizeOpen(true)}
//             className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
//           >
//             <LayoutGrid className="h-4 w-4 text-gray-600" />
//           </button>
//         </div>
//       </div>

//       {/* Division Tabs */}
//       {showDivisionTabs && (
//         <div className="border-b border-gray-200 overflow-x-auto mb-6">
//           <nav className="-mb-px flex space-x-8">
//             <button
//               onClick={() => setActiveTab("all")}
//               className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
//                 activeTab === "all"
//                   ? "border-red-600 text-red-600"
//                   : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
//               }`}
//             >
//               All Divisions
//             </button>

//             {divisions.map((division) => (
//               <button
//                 key={division._id}
//                 onClick={() => setActiveTab(division._id)}
//                 className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
//                   activeTab === division._id
//                     ? "border-red-600 text-red-600"
//                     : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
//                 }`}
//               >
//                 {division.name}
//               </button>
//             ))}
//           </nav>
//         </div>
//       )}

//       {/* Draggable Metric Cards */}
//       {dashboardData && (
//         <DragDropContext onDragEnd={handleDragEnd}>
//           <Droppable droppableId="metrics" direction="horizontal">
//             {(provided) => (
//               <div
//                 ref={provided.innerRef}
//                 {...provided.droppableProps}
//                 // Updated to grid-cols-4 for cleaner responsive support with 4 cards
//                 className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
//               >
//                 {cardOrder.map(
//                   (cardType, index) =>
//                     dashboardData[cardType] &&
//                     !hiddenCards.includes(cardType) && (
//                       <Draggable
//                         key={cardType}
//                         draggableId={cardType}
//                         index={index}
//                       >
//                         {(provided, snapshot) => (
//                           <div
//                             ref={provided.innerRef}
//                             {...provided.draggableProps}
//                             className={`${snapshot.isDragging ? "opacity-50" : ""}`}
//                           >
//                             <div className="relative group">
//                               <div
//                                 {...provided.dragHandleProps}
//                                 className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move"
//                               >
//                                 <GripHorizontal className="h-5 w-5 text-gray-400" />
//                               </div>
//                               {renderMetricCard(
//                                 cardType,
//                                 dashboardData[cardType],
//                               )}
//                             </div>
//                           </div>
//                         )}
//                       </Draggable>
//                     ),
//                 )}
//                 {provided.placeholder}
//               </div>
//             )}
//           </Droppable>
//         </DragDropContext>
//       )}

//       {/* Stage Cards */}
//       {dashboardData?.stages && dashboardData.stages.length > 0 && (
//         <div className="mt-8">
//           <h2 className="text-xl font-semibold text-gray-900 mb-6">
//             {viewType === "user" ? "My Pipeline Stages" : "Stage Pipeline"}
//           </h2>
//           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
//             {dashboardData.stages.map((stage) => (
//               <div
//                 key={stage.stageId}
//                 className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
//                 style={{
//                   borderTopColor: stage.color || "#e5e7eb",
//                   borderTopWidth: "4px",
//                 }}
//               >
//                 <div className="flex items-center justify-between mb-4">
//                   <div className="flex items-center gap-2">
//                     <div
//                       className="w-3 h-3 rounded-full"
//                       style={{ backgroundColor: stage.color || "#e5e7eb" }}
//                     />
//                     <span className="text-sm font-semibold text-gray-700">
//                       #{stage.orderSequence}
//                     </span>
//                   </div>
//                   <span className="text-xs font-medium text-gray-500 uppercase">
//                     {stage.type || "Stage"}
//                   </span>
//                 </div>

//                 <h3 className="font-semibold text-gray-900 mb-1">
//                   {stage.stageName}
//                 </h3>
//                 <p className="text-sm text-gray-500 mb-3">{stage.stageId}</p>

//                 <div className="space-y-2">
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm text-gray-600">Deals</span>
//                     <span className="text-sm font-semibold">
//                       {stage.totalDeals || 0}
//                     </span>
//                   </div>
                  
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm text-gray-600">Revenue</span>
//                     <span className="text-sm font-semibold text-gray-800">
//                       ₹{(stage.totalAmount || 0).toLocaleString("en-IN")}
//                     </span>
//                   </div>

//                   {/* Added dynamic Quotation row per Stage card */}
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm text-gray-600">Quotation</span>
//                     <span className="text-sm font-semibold text-blue-600">
//                       ₹{(stage.quotationValue || 0).toLocaleString("en-IN")}
//                     </span>
//                   </div>
//                 </div>

//                 {stage.totalDeals > 0 && (
//                   <div className="mt-4 pt-4 border-t">
//                     <div className="flex items-center justify-between text-xs">
//                       <span className="text-gray-500">Avg. Deal Size</span>
//                       <span className="font-medium text-gray-700">
//                         ₹
//                         {Math.round(
//                           (stage.totalAmount || 0) / stage.totalDeals,
//                         ).toLocaleString("en-IN")}
//                       </span>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       {/* Customize Panel */}
//       <CustomizePanel
//         isOpen={isCustomizeOpen}
//         onClose={() => setIsCustomizeOpen(false)}
//         hiddenCards={hiddenCards}
//         toggleCardVisibility={toggleCardVisibility}
//         resetLayout={resetLayout}
//       />
      
//       {/* Filter Drawer */}
//       <FilterDrawer
//         isOpen={showFilters}
//         onClose={() => setShowFilters(false)}
//         showUserFilter={viewType !== "user"}
//         users={users}
//         selectedUser={tempUser}
//         onUserChange={setTempUser}
//         showDateFilter={true}
//         filterType={tempFilterType}
//         onFilterTypeChange={setTempFilterType}
//         dateRange={tempDateRange}
//         onDateRangeChange={setTempDateRange}
//         onReset={() => {
//           setTempUser("");
//           setSelectedUser("");
//           setTempFilterType("fiscal");
//           setFilterType("fiscal");
//           setTempDateRange({ from: "", to: "" });
//           setDateRange({ from: "", to: "" });
//           setShowFilters(false);
//         }}
//         onApply={() => {
//           setSelectedUser(tempUser);
//           setFilterType(tempFilterType);
//           setDateRange(tempDateRange);
//         }}
//       />
//     </div>
//   );
// };

// export default DashboardStats;